<template>
  <div class="arrival-time-price-bar bg-white flex justify-between items-center py-4 px-5">
    <div class="price-and-datetime flex flex-col items-start">
      <span class="datetime text-sm" :class="{ 'text-gray-60': !props.bookingItem.length }">
        {{ dateTimeText }}
      </span>
      <span class="price text-sm text-yellow-50 font-500">
        {{ priceText }}
      </span>
    </div>
    <div class="action">
      <nd-button class="min-w-86px" type="primary" :disabled="!isSubmittable" @click="emit('submit')">{{ $t('all_confirm') }}</nd-button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { getUtcDate } from '@/utils/time'

import type { UiProductSelectedData } from '@/types/product'

const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    currencyCode?: string
    bookingItem: UiProductSelectedData[]
    isCrossDay?: boolean
    checkOutTime?: string
    timezone?: string
  }>(),
  {
    currencyCode: 'TWD',
    isCrossDay: false,
    checkOutTime: '00:00:00',
    timezone: 'Asia/Taipei',
  }
)

const emit = defineEmits<{
  (event: 'submit'): void
}>()

const { formatInTimeZone } = await useI18nDateFns()

const bookingItem = computed(() => props.bookingItem)
const isCrossDay = computed(() => props.isCrossDay)

const dateTimeText = computed(() => {
  const startItem = bookingItem.value?.at?.(0)
  if (!startItem?.dateTime) return t('product_select_time_place_holder')

  const startDate = getUtcDate(startItem.dateTime, props.timezone)
  let result = `${formatInTimeZone(startDate, props.timezone, 'yyyy/MM/dd HH:mm (zzz)')}`

  // 處理跨日商品沒跨日的情況
  if (isCrossDay.value && bookingItem.value.length === 1) {
    const [startDateString] = startItem.dateTime.split(' ')
    const endDate = getUtcDate(`${startDateString} ${props.checkOutTime}`, props.timezone)
    result = `${result} ~ ${formatInTimeZone(endDate, props.timezone, 'yyyy/MM/dd HH:mm (zzz)')}`
  } else {
    const endItem = bookingItem.value?.at?.(-1)
    if (!endItem?.dateTime || startItem === endItem) return result

    const endDate = getUtcDate(endItem.dateTime, props.timezone)
    result += ` ~ ${formatInTimeZone(endDate, props.timezone, 'yyyy/MM/dd HH:mm (zzz)')}`
  }

  return result
})

const priceText = computed(() => {
  const totalPrice = props.bookingItem.reduce((acc, item) => acc + item.price, 0)
  return `${props.currencyCode} ${priceFormat(totalPrice)}`
})

const isSubmittable = computed(() => props.bookingItem.length > 0)
</script>

<style lang="scss" scoped>
.arrival-time-price-bar {
  box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.1);
}
</style>
